import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import { useUpdateVendorDetails } from 'src/queries/vendors/vendors.queries'
import type {
  UpdateVendorDetailsRequest,
  Vendor,
} from 'src/queries/vendors/vendors.types'
import {
  BackendErrorPayload,
  getBackendErrorPayload,
  NETWORK_ERROR_PAYLOAD,
  parseBackendErrors,
} from 'src/utils/error.utils'

import { vendorFieldErrorMessage } from './vendorErrorMessages'
import {
  isVendorFormValid,
  validateVendorForm,
  VendorField,
  VendorFormValues,
} from './vendorForm.validation'

/**
 * Vendor-details acknowledgement (EX-40771). A SECOND acknowledgement, separate
 * from the ACH `VENDOR_ACKNOWLEDGEMENT` in VendorFlyout — it needs its own copy
 * and its own version so the recorded version says which words the user agreed
 * to. Every details edit requires a fresh re-acknowledgement (PO 2026-08-18),
 * so this version rides on every details PATCH.
 */
export const VENDOR_DETAILS_ACKNOWLEDGEMENT = {
  version: '2026-08-18',
  heading: 'I acknowledge that...',
  description: 'These vendor details are accurate and up to date.',
} as const

const DEFAULT_PHONE_ISO_COUNTRY = 'US'

/** Free-text fields the tab edits directly (phone has its own split setters). */
export const VENDOR_TEXT_FIELDS = [
  'name',
  'email',
  'externalId',
  'contactName',
  'notes',
] as const

export type VendorTextField =
  | 'name'
  | 'email'
  | 'externalId'
  | 'contactName'
  | 'notes'

/** Server (wire) field name -> our domain field, so a backend `details[]` entry
 * lands under the right input (`phoneNumber` -> phone, `description` -> notes). */
const WIRE_TO_DOMAIN_FIELD: Record<string, VendorField> = {
  name: 'name',
  email: 'email',
  externalId: 'externalId',
  contactName: 'contactName',
  phoneNumber: 'phone',
  description: 'notes',
}

const trimmedOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** Split a stored E.164 number into the national-number + ISO-country shape the
 * add form's phone controls use, so the picker prefills to the right country. */
const splitPhone = (
  e164: string | undefined,
): { phone: string; phoneIsoCountry: string } => {
  if (!e164) return { phone: '', phoneIsoCountry: DEFAULT_PHONE_ISO_COUNTRY }
  const parsed = parsePhoneNumberFromString(e164)
  if (parsed?.country && parsed.nationalNumber) {
    return {
      phone: String(parsed.nationalNumber),
      phoneIsoCountry: parsed.country,
    }
  }
  // Unparseable stored value (server normalizes to E.164, so this is rare) —
  // fall back to empty so the field is self-consistent and not phantom-dirty.
  return { phone: '', phoneIsoCountry: DEFAULT_PHONE_ISO_COUNTRY }
}

/** Render a stored E.164 number the way the editable field shows it, so an
 *  Archived (read-only) vendor and an Active one display the same number
 *  identically instead of `+12125551212` vs `(212) 555-1212`. Falls back to the
 *  raw value if it will not parse — better a stored number in an odd format
 *  than a blank field. */
export const formatPhoneForDisplay = (e164: string | undefined): string => {
  if (!e164) return ''
  return parsePhoneNumberFromString(e164)?.formatNational() ?? e164
}

const composePhone = (phone: string, iso: string): string | undefined => {
  const trimmed = phone.trim()
  if (trimmed === '') return undefined
  return `+${getCountryCallingCode(iso as CountryCode)}${trimmed}`
}

const initialValues = (vendor: Vendor): VendorFormValues => {
  const { phone, phoneIsoCountry } = splitPhone(vendor.phone)
  return {
    name: vendor.name ?? '',
    email: vendor.email ?? '',
    externalId: vendor.externalId ?? '',
    contactName: vendor.contactName ?? '',
    phone,
    phoneIsoCountry,
    notes: vendor.notes ?? '',
  }
}

/** Extract a usable backend payload from either a raw AxiosError (`.response`)
 * or a normalized QueryError (which has no `.response`, only `error`/`status`
 * and a non-enumerable `originalError`) — see src/queries/AGENTS.md. */
const toBackendPayload = (error: unknown): BackendErrorPayload<string> => {
  const raw =
    (error as { originalError?: unknown } | null)?.originalError ?? error
  const fromResponse = getBackendErrorPayload(raw)
  if (fromResponse !== NETWORK_ERROR_PAYLOAD) return fromResponse
  if (error && typeof error === 'object' && 'error' in error) {
    return error as BackendErrorPayload<string>
  }
  return fromResponse
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object') {
    const e = error as {
      status?: number
      response?: { status?: number }
    }
    if (typeof e.status === 'number') return e.status
    if (typeof e.response?.status === 'number') return e.response.status
  }
  return undefined
}

/** A 422 whose code/message names the acknowledgement, and that carries no
 * per-field `details[]` — the account-collection confirm route's
 * `AcknowledgementRequired` shape. Field-validation 422s (with `details[]`) are
 * NOT this; they map to fields. */
const isAcknowledgementError = (
  payload: BackendErrorPayload<string>,
  status: number | undefined,
): boolean => {
  if (status !== 422) return false
  if (payload.details && payload.details.length > 0) return false
  const code = payload.errorCode ?? ''
  return /acknowledg/i.test(code) || /acknowledg/i.test(payload.error ?? '')
}

export interface VendorDetailsFormState {
  values: VendorFormValues
  setTextField: (field: VendorTextField, value: string) => void
  setPhone: (value: string) => void
  setPhoneIsoCountry: (iso: string) => void
  /** Localized message for a field, or undefined. Local (validation) wins over
   * a server error; required errors stay hidden until the field is edited or a
   * save is attempted, so a pristine prefilled form shows nothing. */
  fieldError: (field: VendorField) => string | undefined
  isDirty: boolean
  isValid: boolean
  acknowledged: boolean
  setAcknowledged: (value: boolean) => void
  canSave: boolean
  isSaving: boolean
  acknowledgementError?: string
  topLevelError?: string
  submit: () => void
}

/**
 * Owns the vendor-details edit form: live values seeded from the vendor, a
 * six-field dirty check (deliberately including externalId + notes — see the
 * PR; our PATCH is a full replace, so excluding them would silently drop an
 * edit), the acknowledgement gate, and the PATCH submit with server-error
 * mapping. Validation is recomputed every render (KYB pattern); only visibility
 * lives in state. Mount this fresh per open (key it) so the acknowledgement and
 * any stale server errors never carry across sessions.
 */
export function useVendorDetailsForm(params: {
  vendor: Vendor
  organizationId: string
  onSaved: (vendor: Vendor) => void
}): VendorDetailsFormState {
  const { vendor, organizationId, onSaved } = params
  const translate = useTranslate()

  const [baseline, setBaseline] = useState<VendorFormValues>(() =>
    initialValues(vendor),
  )
  const [values, setValues] = useState<VendorFormValues>(baseline)
  // The server snapshot the current `values` were seeded from. Compared against
  // in the rebase below; not state, because it must not drive a render.
  const seededFromRef = useRef<VendorFormValues>(baseline)

  // `vendorQueries.detail` is stale-on-mount, so reopening a cached vendor
  // renders the CACHED object and swaps in the refetched one a beat later —
  // and VendorEditLoaded is keyed only by vendor id, so it does not remount.
  // This PATCH is a FULL REPLACE, so both halves of the reconcile matter: keep
  // the user's pending edits, and let every other field follow the server, or a
  // save ships stale values over newer ones.
  //
  // A field has a pending edit iff it DIFFERS from the server value it was
  // seeded with. That is derived, so it is always current — reverting an edit
  // makes the field equal again and hands it straight back to the server, with
  // nothing to remember to clear.
  useEffect(() => {
    const fresh = initialValues(vendor)
    const seededFrom = seededFromRef.current
    seededFromRef.current = fresh
    setBaseline(fresh)
    setValues((prev) => {
      const next = { ...fresh }
      VENDOR_TEXT_FIELDS.forEach((field) => {
        if (prev[field] !== seededFrom[field]) next[field] = prev[field]
      })
      // The number and its country compose one wire value, so they are pending
      // (or not) together — comparing them separately would strand a country
      // change whose national number happens to match.
      if (
        composePhone(prev.phone, prev.phoneIsoCountry) !==
        composePhone(seededFrom.phone, seededFrom.phoneIsoCountry)
      ) {
        next.phone = prev.phone
        next.phoneIsoCountry = prev.phoneIsoCountry
      }
      return next
    })
  }, [vendor])
  const [acknowledged, setAcknowledgedState] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [serverFieldErrors, setServerFieldErrors] = useState<
    Partial<Record<VendorField, string>>
  >({})
  const [acknowledgementError, setAcknowledgementError] = useState<
    string | undefined
  >(undefined)
  const [topLevelError, setTopLevelError] = useState<string | undefined>(
    undefined,
  )

  const updateMutation = useUpdateVendorDetails(organizationId, {
    onSuccess: (savedVendor) => {
      onSaved(savedVendor)
    },
  })

  const clearServerErrors = useCallback((): void => {
    setServerFieldErrors({})
    setAcknowledgementError(undefined)
    setTopLevelError(undefined)
  }, [])

  const setTextField = useCallback(
    (field: VendorTextField, value: string): void => {
      setValues((prev) => ({ ...prev, [field]: value }))
      // Clear a stale server error for the field the user is now fixing.
      setServerFieldErrors((prev) =>
        prev[field] === undefined ? prev : { ...prev, [field]: undefined },
      )
    },
    [],
  )

  const setPhone = useCallback((value: string): void => {
    setValues((prev) => ({ ...prev, phone: value }))
    setServerFieldErrors((prev) =>
      prev.phone === undefined ? prev : { ...prev, phone: undefined },
    )
  }, [])

  const setPhoneIsoCountry = useCallback((iso: string): void => {
    setValues((prev) => ({ ...prev, phoneIsoCountry: iso }))
    // The country is half of the submitted phone number, so changing it is an
    // edit to the phone field — and can be the very thing that fixes a server
    // rejection. Clear the stale error the same way setPhone does, or the user
    // corrects the problem and the message stays put.
    setServerFieldErrors((prev) =>
      prev.phone === undefined ? prev : { ...prev, phone: undefined },
    )
  }, [])

  const setAcknowledged = useCallback((value: boolean): void => {
    setAcknowledgedState(value)
    if (value) setAcknowledgementError(undefined)
  }, [])

  const errors = useMemo(() => validateVendorForm(values), [values])
  const isValid = isVendorFormValid(errors)

  const isDirty = useMemo(
    () =>
      values.name !== baseline.name ||
      values.email !== baseline.email ||
      values.externalId !== baseline.externalId ||
      values.contactName !== baseline.contactName ||
      values.notes !== baseline.notes ||
      composePhone(values.phone, values.phoneIsoCountry) !==
        composePhone(baseline.phone, baseline.phoneIsoCountry),
    [values, baseline],
  )

  const fieldIsDirty = useCallback(
    (field: VendorField): boolean => {
      if (field === 'phone') {
        return (
          composePhone(values.phone, values.phoneIsoCountry) !==
          composePhone(baseline.phone, baseline.phoneIsoCountry)
        )
      }
      return values[field] !== baseline[field]
    },
    [values, baseline],
  )

  const fieldError = useCallback(
    (field: VendorField): string | undefined => {
      const code = errors[field]
      if (code) {
        const isRequired = code === 'NAME_REQUIRED' || code === 'EMAIL_REQUIRED'
        const visible = isRequired ? attempted || fieldIsDirty(field) : true
        if (visible) return vendorFieldErrorMessage(translate, code)
      }
      return serverFieldErrors[field]
    },
    [errors, attempted, fieldIsDirty, translate, serverFieldErrors],
  )

  const isSaving = updateMutation.isPending
  const canSave = isDirty && isValid && acknowledged && !isSaving

  const submit = useCallback((): void => {
    setAttempted(true)
    if (!isValid || !acknowledged || !isDirty || isSaving) return
    clearServerErrors()

    const request: UpdateVendorDetailsRequest = {
      name: values.name.trim(),
      email: values.email.trim(),
      externalId: trimmedOrUndefined(values.externalId),
      contactName: trimmedOrUndefined(values.contactName),
      phone: composePhone(values.phone, values.phoneIsoCountry),
      notes: trimmedOrUndefined(values.notes),
    }

    updateMutation.mutate(
      {
        vendorId: vendor.id,
        request,
        acknowledged: true,
        acknowledgementCopyVersion: VENDOR_DETAILS_ACKNOWLEDGEMENT.version,
      },
      {
        onError: (error) => {
          const payload = toBackendPayload(error)
          const status = getErrorStatus(error)
          if (isAcknowledgementError(payload, status)) {
            setAcknowledgementError(
              translate(
                'We could not record your acknowledgement. Please try again.',
              ),
            )
            return
          }
          const { topLevelError: top, errorDetailMap } =
            parseBackendErrors(payload)
          if (errorDetailMap) {
            const mapped: Partial<Record<VendorField, string>> = {}
            Object.entries(errorDetailMap).forEach(([wireField, messages]) => {
              const domainField = WIRE_TO_DOMAIN_FIELD[wireField]
              if (domainField && messages[0]) {
                mapped[domainField] = messages[0]
              }
            })
            setServerFieldErrors(mapped)
            // Any message with no field attribution still needs a home.
            const hadUnmappedTop =
              Object.keys(mapped).length === 0 && top !== undefined
            if (hadUnmappedTop) setTopLevelError(top)
          } else {
            // No details[] (today's controller shape) — fall back to the
            // top-level message under the last-resort footer line.
            setTopLevelError(top)
          }
        },
      },
    )
  }, [
    isValid,
    acknowledged,
    isDirty,
    isSaving,
    clearServerErrors,
    values,
    updateMutation,
    vendor.id,
    translate,
  ])

  return {
    values,
    setTextField,
    setPhone,
    setPhoneIsoCountry,
    fieldError,
    isDirty,
    isValid,
    acknowledged,
    setAcknowledged,
    canSave,
    isSaving,
    acknowledgementError,
    topLevelError,
    submit,
  }
}
